import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { brregUrl, entityFlags, formatOrgNumber, isValidOrgNumber, lookupOrg, orgDigits, searchOrg } from "@/lib/brreg";
import { Building2, Check, Loader2, Search, ExternalLink, AlertTriangle, RefreshCw, MapPin, Landmark } from "lucide-react";
import { cn } from "@/lib/utils";

const TONE = {
  ok: "status-betalt",
  warn: "status-forfaller_snart",
  danger: "status-forfalt",
  muted: "status-avvist",
};

const FlagPills = ({ entity }) => {
  const flags = entityFlags(entity);
  if (!flags.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="brreg-flags">
      {flags.map((f) => <span key={f.key} className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium", TONE[f.tone])} data-testid={`brreg-flag-${f.key}`}>{f.label}</span>)}
    </div>
  );
};

const addressLine = (entity) => {
  const a = entity?.address;
  if (!a) return null;
  return [a.address, [a.postal_code, a.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
};

/**
 * Org.nr-felt med oppslag i Brønnøysundregistrene.
 * - Slår opp automatisk når 9 gyldige siffer er tastet (MOD11), eller på knappen «Hent».
 * - Viser treffkort med «Bruk opplysningene» (onApply(entity)). Med autoApply=true fylles skjemaet rett inn.
 */
export const BrregOrgField = ({ id = "org_number", value, onChange, onApply, autoApply = false, appliedOrgNumber = "", disabled = false, className, inputClassName, testIdPrefix = "brreg", placeholder = "9 siffer" }) => {
  const [state, setState] = useState({ status: "idle", entity: null, error: "", org: "" });
  const [applied, setApplied] = useState("");
  const timer = useRef(null);
  const digits = orgDigits(value);
  const complete = digits.length === 9;
  const valid = complete && isValidOrgNumber(digits);

  const run = useCallback(async (org, { refresh = false, apply = false } = {}) => {
    setState({ status: "loading", entity: null, error: "", org });
    try {
      const entity = await lookupOrg(org, { refresh });
      setState({ status: "found", entity, error: "", org });
      if (apply && onApply) { onApply(entity); setApplied(org); }
    } catch (e) {
      const status = e?.response?.status;
      setState({ status: status === 404 ? "notfound" : "error", entity: null, error: errorMessage(e, "Oppslaget mislyktes"), org });
    }
  }, [onApply]);

  // Auto-oppslag når gyldig org.nr er komplett
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!valid || disabled) {
      if (!complete && state.status !== "idle") setState({ status: "idle", entity: null, error: "", org: "" });
      return undefined;
    }
    if (state.org === digits && state.status !== "idle") return undefined;
    timer.current = setTimeout(() => run(digits, { apply: autoApply }), 350);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits, valid, disabled]);

  const entity = state.entity;
  const isApplied = !!entity && (applied === entity.org_number || orgDigits(appliedOrgNumber) === entity.org_number);

  return (
    <div className={cn("space-y-2", className)} data-testid={`${testIdPrefix}-org-field`}>
      <div className="flex gap-2">
        <Input
          id={id}
          inputMode="numeric"
          autoComplete="off"
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={() => { if (complete && value !== formatOrgNumber(value)) onChange?.(formatOrgNumber(value)); }}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={complete && !valid ? true : undefined}
          className={cn("tnum", complete && !valid && "border-destructive focus-visible:ring-destructive", inputClassName)}
          data-testid={`${testIdPrefix}-org-input`}
        />
        <Button type="button" variant="outline" onClick={() => run(digits, { refresh: state.status === "found" })} disabled={disabled || !valid || state.status === "loading"} className="shrink-0" title="Hent opplysninger fra Brønnøysundregistrene" data-testid={`${testIdPrefix}-org-lookup`}>
          {state.status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Landmark className="h-4 w-4" />}
          <span className="ml-2 hidden sm:inline">{state.status === "found" ? "Oppdater" : "Hent fra BRREG"}</span>
        </Button>
      </div>

      {complete && !valid ? (
        <p className="text-xs text-destructive flex items-center gap-1" role="alert" data-testid={`${testIdPrefix}-org-invalid`}><AlertTriangle className="h-3.5 w-3.5" /> Ugyldig organisasjonsnummer – kontrollsifferet stemmer ikke.</p>
      ) : state.status === "loading" ? (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`${testIdPrefix}-org-loading`}><Loader2 className="h-3.5 w-3.5 animate-spin" /> Søker i Enhetsregisteret …</p>
      ) : state.status === "notfound" ? (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`${testIdPrefix}-org-notfound`}><Search className="h-3.5 w-3.5" /> Fant ingen virksomhet med org.nr {formatOrgNumber(state.org)} i Enhetsregisteret. Du kan fylle inn manuelt.</p>
      ) : state.status === "error" ? (
        <p className="text-xs text-destructive flex items-center gap-1.5" role="alert" data-testid={`${testIdPrefix}-org-error`}><AlertTriangle className="h-3.5 w-3.5" /> {state.error} <button type="button" className="underline" onClick={() => run(digits)}>Prøv igjen</button></p>
      ) : entity ? (
        <div className={cn("rounded-lg border p-3 flex flex-col gap-2", isApplied ? "bg-secondary" : "bg-accent/60 border-primary/30")} data-testid={`${testIdPrefix}-org-result`}>
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-card border flex items-center justify-center shrink-0"><Building2 className="h-4 w-4 text-primary" /></div>
            <div className="min-w-0 flex-1">
              <p className="font-medium leading-tight" data-testid={`${testIdPrefix}-org-result-name`}>{entity.name}</p>
              <p className="text-xs text-muted-foreground">{entity.entity_form?.description || entity.entity_form?.code}{entity.org_number_display ? ` · Org.nr ${entity.org_number_display}` : ""}</p>
              {addressLine(entity) ? <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3 shrink-0" /> {addressLine(entity)}</p> : <p className="text-xs text-muted-foreground mt-0.5">Ingen adresse registrert</p>}
              <div className="mt-1.5"><FlagPills entity={entity} /></div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onApply ? (
              isApplied ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-success" data-testid={`${testIdPrefix}-org-applied`}><Check className="h-3.5 w-3.5" /> Opplysningene er fylt inn</span>
              ) : (
                <Button type="button" size="sm" onClick={() => { onApply(entity); setApplied(entity.org_number); }} disabled={disabled} data-testid={`${testIdPrefix}-org-apply`}><Check className="h-4 w-4 mr-1.5" /> Bruk opplysningene</Button>
              )
            ) : null}
            {isApplied ? <Button type="button" variant="ghost" size="sm" onClick={() => { onApply(entity); }} data-testid={`${testIdPrefix}-org-reapply`}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Fyll inn igjen</Button> : null}
            <a href={brregUrl(entity.org_number)} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`${testIdPrefix}-org-brreg-link`}>Se i BRREG <ExternalLink className="h-3 w-3" /></a>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Skriv inn organisasjonsnummeret, så henter vi navn og adresse fra Brønnøysundregistrene.</p>
      )}
    </div>
  );
};

/**
 * Navnefelt med søk i Enhetsregisteret (combobox). onSelect(entity) når bruker velger et treff.
 * Fungerer som vanlig tekstfelt hvis bruker ikke velger noe.
 */
export const BrregNameSearch = ({ id = "name", value, onChange, onSelect, disabled = false, placeholder = "Firmanavn", className, inputClassName, testIdPrefix = "brreg", minChars = 3, required }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [error, setError] = useState("");
  const timer = useRef(null);
  const typedRef = useRef(false); // søk kun når brukeren selv skriver (ikke ved programmatisk utfylling)
  const wrapRef = useRef(null);
  const listId = `${id}-brreg-list`;

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!typedRef.current) return undefined;
    typedRef.current = false;
    const q = (value || "").trim();
    if (disabled || q.length < minChars) { setItems([]); setOpen(false); setLoading(false); return undefined; }
    setItems([]);
    setLoading(true);
    setOpen(true);
    timer.current = setTimeout(async () => {
      setError("");
      try {
        const res = await searchOrg(q, 8);
        setItems(res);
        setOpen(true);
        setActive(res.length ? 0 : -1);
      } catch (e) {
        setItems([]); setError(errorMessage(e, "Søket mislyktes")); setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [value, disabled, minChars]);

  // Lukk ved klikk utenfor
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const choose = (entity) => {
    typedRef.current = false;
    setOpen(false);
    setLoading(false);
    setItems([]);
    onSelect?.(entity);
  };

  const onKeyDown = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(items[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const showList = open && !disabled;
  const hint = useMemo(() => ((value || "").trim().length < minChars ? `Skriv minst ${minChars} tegn for å søke i Enhetsregisteret` : ""), [value, minChars]);

  return (
    <div ref={wrapRef} className={cn("relative", className)} data-testid={`${testIdPrefix}-name-search`}>
      <div className="relative">
        <Input
          id={id}
          value={value || ""}
          onChange={(e) => { typedRef.current = true; onChange?.(e.target.value); }}
          onKeyDown={onKeyDown}
          onFocus={() => { if (items.length) setOpen(true); }}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete="off"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          className={cn("pr-9", inputClassName)}
          data-testid={`${testIdPrefix}-name-input`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</span>
      </div>
      {hint && !showList ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      {showList ? (
        <ul id={listId} role="listbox" className="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-lg border bg-card shadow-lg backdrop-blur" data-testid={`${testIdPrefix}-name-results`}>
          {error ? (
            <li className="px-3 py-2.5 text-sm text-destructive">{error}</li>
          ) : loading ? (
            <li className="px-3 py-2.5 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Søker i Enhetsregisteret …</li>
          ) : items.length === 0 ? (
            <li className="px-3 py-2.5 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-name-empty`}>Ingen treff i Enhetsregisteret. Du kan skrive navnet manuelt.</li>
          ) : items.map((it, i) => (
            <li key={`${it.org_number}-${i}`} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(it)}
                onMouseEnter={() => setActive(i)}
                className={cn("w-full text-left px-3 py-2 flex items-start gap-3 transition-colors", i === active ? "bg-accent text-accent-foreground" : "hover:bg-secondary")}
                data-testid={`${testIdPrefix}-name-option-${it.org_number}`}
              >
                <Building2 className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium truncate">{it.name}</span>
                  <span className="block text-xs text-muted-foreground truncate">Org.nr {it.org_number_display}{it.entity_form?.code ? ` · ${it.entity_form.code}` : ""}{it.address?.city ? ` · ${it.address.city}` : ""}</span>
                </span>
              </button>
            </li>
          ))}
          <li className="px-3 py-1.5 text-[11px] text-muted-foreground border-t flex items-center gap-1"><Landmark className="h-3 w-3" /> Kilde: Brønnøysundregistrene</li>
        </ul>
      ) : null}
    </div>
  );
};
